/**
 * Drop tables, as the tools think about them.
 *
 * One list of lines rather than the two halves the server stores, and a reading
 * of what a hundred kills would actually produce, which is the only form anybody
 * can judge a table in. Weights on their own tell you nothing: forty against a
 * hundred and forty against three hundred are different drops.
 */

import type { DropEntryWire } from '@inverse/protocol';
import type { Condition } from './Dialogue';

export interface DropPreview {
  /** What to call it: an item name, or nothing at all. */
  name: string;
  /** How many of the hundred kills give this line. */
  times: number;
  /** The count handed over when it does, as it should be read. */
  amount: string;
  everyKill: boolean;
}

/** Total weight across the rolled lines, which is what one roll is measured against. */
export function rolledWeight(entries: readonly DropEntryWire[]): number {
  return entries.reduce((total, entry) => total + (entry.everyKill ? 0 : entry.weight), 0);
}

/** The chance of one rolled line coming up, as a percentage of a kill. */
export function chanceOf(entry: DropEntryWire, entries: readonly DropEntryWire[]): number {
  if (entry.everyKill) return 100;

  const total = rolledWeight(entries);
  return total > 0 ? (entry.weight / total) * 100 : 0;
}

/**
 * What a hundred kills look like.
 *
 * Expected counts rather than a simulation, so the same table always reads the
 * same way. A table being judged should not move while it is being read.
 */
export function hundredKills(
  entries: readonly DropEntryWire[],
  nameOf: (itemId: number) => string,
): DropPreview[] {
  return entries.map((entry) => ({
    name: entry.itemId > 0 ? nameOf(entry.itemId) : 'Nothing',
    times: Math.round(chanceOf(entry, entries)),
    amount: entry.min === entry.max ? String(entry.min) : `${entry.min} to ${entry.max}`,
    everyKill: entry.everyKill,
  }));
}

/** One line of a further roll, which is the same shape the world stores. */
export interface RollEntry {
  itemId: number | null;
  min: number;
  max: number;
  weight: number;
}

/** A roll of its own: its own odds, its own conditions, its own lines. */
export interface Roll {
  name: string;
  chance?: number;
  when: Condition[];
  entries: RollEntry[];
}

/**
 * The rolls a table carries, read from what the server sent.
 *
 * Anything unreadable comes back as no rolls at all rather than throwing, so a
 * table somebody edited by hand cannot lock the panel that would fix it.
 */
export function rollsOf(json: string): Roll[] {
  try {
    const held: unknown = JSON.parse(json || '[]');
    if (!Array.isArray(held)) return [];

    return held.map((one) => {
      const roll = one as Partial<Roll>;
      return {
        name: typeof roll.name === 'string' ? roll.name : '',
        chance: typeof roll.chance === 'number' ? roll.chance : undefined,
        when: Array.isArray(roll.when) ? roll.when : [],
        entries: Array.isArray(roll.entries)
          ? roll.entries.map((entry) => ({
              itemId: typeof entry.itemId === 'number' ? entry.itemId : null,
              min: Math.max(1, Math.round(entry.min ?? 1)),
              max: Math.max(1, Math.round(entry.max ?? entry.min ?? 1)),
              weight: Math.max(1, Math.round(entry.weight ?? 1)),
            }))
          : [],
      };
    });
  } catch {
    return [];
  }
}

/**
 * How often one line of a roll actually comes up, as one in this many kills.
 *
 * The whole reason a rare table is worth having: the number a designer wants to
 * pick is "one in a thousand", not "weight nine against a hundred and forty
 * thousand". Zero means never.
 */
export function oneIn(roll: Roll, entry: RollEntry): number {
  const total = roll.entries.reduce((sum, one) => sum + Math.max(1, one.weight), 0);
  if (total <= 0 || entry.weight <= 0) return 0;

  return Math.round((Math.max(1, roll.chance ?? 1) * total) / entry.weight);
}
